import type { ComponentType } from "react";

import {
  type ClinicalIconProps,
  IconAdditionalImpression,
  IconBridge,
  IconBridgeOnImplant,
  IconBruxismSplint,
  IconCompleteDenture,
  IconCrown,
  IconCrownOnImplant,
  IconCustomTray,
  IconDentureRepair,
  IconDiagnosticCategory,
  IconDigitalDesign,
  IconFixedRestorationCategory,
  IconImplantCategory,
  IconInlayCore,
  IconInlayOnlay,
  IconOrthoStudyModel,
  IconOrthodonticsCategory,
  IconPartialDenture,
  IconRemovableCategory,
  IconRetentionWire,
  IconSpaceMaintainer,
  IconSplintCategory,
  IconToothAddition,
  IconVeneer,
  IconWaxBite,
  IconWaxUp,
} from "@jasper/forms";
import { ProductTypeV2 } from "/@/generated/graphql";

export type ProductChoice = {
  type: ProductTypeV2;
  label: string;
  description: string;
  icon: ComponentType<ClinicalIconProps>;
  tag?: string;
  subcategory?: string;
};

export type ProductCategory = {
  icon: ComponentType<ClinicalIconProps>;
  label: string;
  choices: ProductChoice[];
  hasSubCategories: boolean;
};

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  {
    label: "Restauration fixe",
    icon: IconFixedRestorationCategory,
    hasSubCategories: true,
    choices: [
      {
        type: ProductTypeV2.Crown,
        label: "Couronne(s)",
        description: "Céramique sur zircone, full zircone, e.max",
        icon: IconCrown,
        subcategory: "Sur dents naturelles",
      },
      {
        type: ProductTypeV2.Lay,
        label: "Inlay / Onlay",
        description: "Composite, céramique, zircone",
        icon: IconInlayOnlay,
        subcategory: "Sur dents naturelles",
      },
      {
        type: ProductTypeV2.Bridge,
        label: "Bridge",
        description: "Piliers dents naturelles · pontiques",
        icon: IconBridge,
        subcategory: "Sur dents naturelles",
      },
      {
        type: ProductTypeV2.Veneer,
        label: "Facette",
        description: "Composite, e.max, full zircone",
        icon: IconVeneer,
        subcategory: "Sur dents naturelles",
      },
      {
        type: ProductTypeV2.InlayCore,
        label: "Inlay-core",
        description: "Faux moignon, avec ou sans couronne",
        icon: IconInlayCore,
        subcategory: "Sur inlay-core",
      },
    ],
  },
  {
    label: "Sur implant",
    icon: IconImplantCategory,
    hasSubCategories: false,
    choices: [
      {
        type: ProductTypeV2.ImplantCrown,
        label: "Couronne(s) sur implant",
        description: "Unitaire ou solidarisées",
        icon: IconCrownOnImplant,
        tag: "Fixe",
      },
      {
        type: ProductTypeV2.BridgeImplant,
        label: "Bridge sur implant",
        description: "Piliers implants · pontiques",
        icon: IconBridgeOnImplant,
        tag: "Fixe",
      },
    ],
  },
  {
    label: "Amovible",
    icon: IconRemovableCategory,
    hasSubCategories: false,
    choices: [
      {
        type: ProductTypeV2.PartialDenture,
        label: "Amovible partielle",
        description: "Stellite, résine, Valplast",
        icon: IconPartialDenture,
      },
      {
        type: ProductTypeV2.FullDenture,
        label: "Amovible complète",
        description: "Arcade totale",
        icon: IconCompleteDenture,
      },
      {
        type: ProductTypeV2.AddingTeethOnExistingDenture,
        label: "Adjonctions de dents",
        description: "Sur prothèse existante",
        icon: IconToothAddition,
      },
      {
        type: ProductTypeV2.DentureRepair,
        label: "Réparation prothèse",
        description: "Amovible, fracture ou crochet",
        icon: IconDentureRepair,
      },
    ],
  },
  {
    label: "Gouttières",
    icon: IconSplintCategory,
    hasSubCategories: false,
    choices: [
      {
        type: ProductTypeV2.Splint,
        label: "Gouttière de bruxisme",
        description: "Dure, semi-rigide ou souple",
        icon: IconBruxismSplint,
      },
    ],
  },
  {
    label: "Diagnostic & accessoires",
    icon: IconDiagnosticCategory,
    hasSubCategories: false,
    choices: [
      {
        type: ProductTypeV2.SpaceMaintainer,
        label: "Mainteneur d'espace",
        description: "Édentement temporaire",
        icon: IconSpaceMaintainer,
      },
      {
        type: ProductTypeV2.RetentionWire,
        label: "Fil de contention",
        description: "Collé, post-orthodontie",
        icon: IconRetentionWire,
      },
      {
        type: ProductTypeV2.OrthoModel,
        label: "Modèle étude ortho",
        description: "Analyse et archivage",
        icon: IconOrthoStudyModel,
      },
      {
        type: ProductTypeV2.CustomTray,
        label: "Porte-empreinte individuel",
        description: "Sur mesure",
        icon: IconCustomTray,
      },
      {
        type: ProductTypeV2.WaxBite,
        label: "Cire d'occlusion",
        description: "Enregistrement du rapport",
        icon: IconWaxBite,
      },
      {
        type: ProductTypeV2.ScanOnly,
        label: "Design digital",
        description: "Conception seule, sans fabrication",
        icon: IconDigitalDesign,
      },
      {
        type: ProductTypeV2.AdditionalImpressionForRemovableProsthesis,
        label: "Empreinte supplémentaire",
        description: "Pour prothèse amovible",
        icon: IconAdditionalImpression,
      },
      {
        type: ProductTypeV2.WaxUp,
        label: "Wax-up / Mock-up",
        description:
          "Digital, modèle imprimé ou non, clé ou gouttière à injection",
        icon: IconWaxUp,
      },
    ],
  },
  {
    label: "Orthodontie",
    icon: IconOrthodonticsCategory,
    hasSubCategories: false,
    choices: [
      {
        type: ProductTypeV2.SpaceMaintainer,
        label: "Mainteneur d'espace",
        description: "Bague et boucle",
        icon: IconSpaceMaintainer,
      },
    ],
  },
];

const CATEGORY_DESCRIPTION_SIZE = 3;

export const getCategoryDescription = (category: ProductCategory): string => {
  const description = category.choices
    .slice(0, CATEGORY_DESCRIPTION_SIZE)
    .map((choice, index) =>
      index === 0 ? choice.label : choice.label.toLowerCase()
    )
    .join(", ");

  return category.choices.length > CATEGORY_DESCRIPTION_SIZE
    ? `${description}…`
    : description;
};
